import React, { useCallback, useEffect, useState } from 'react'
import { getNotificationEmailLogs } from '../../api/adminNotificationsService'

// Hard-coded list of known event types produced by the notifications engine.
// This prevents the dropdown from showing only the event types present on the
// currently loaded page of results.
const KNOWN_EVENT_TYPES = [
  'availability.missing',
  'availability.overlap_found',
  'proposal.received',
  'proposal.expiring',
]

function NotificationEmailActivityPanel() {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [eventType, setEventType] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  })

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const result = await getNotificationEmailLogs({
        search,
        status,
        eventType,
        startDate,
        endDate,
        page,
        limit: 25,
      })
      setLogs(result.logs || [])
      setPagination(result.pagination || { page: 1, totalPages: 1, total: 0 })
    } catch (err) {
      console.error('Failed to load notification email logs', err)
      setError(err.message || 'Failed to load notification email logs')
    } finally {
      setLoading(false)
    }
  }, [search, status, eventType, startDate, endDate, page])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h5 className="mb-1">Auto-Triggered Notification Emails</h5>
            <p className="text-muted small mb-0">
              This view shows all email notifications logged by the
              notifications engine (not just weekly outbox drafts).
            </p>
          </div>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={loadLogs}
          >
            Refresh
          </button>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-12 col-md-4">
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Search recipient, event type, league..."
              value={search}
              onChange={(e) => {
                setPage(1)
                setSearch(e.target.value)
              }}
            />
          </div>
          <div className="col-6 col-md-2">
            <select
              className="form-select form-select-sm"
              value={status}
              onChange={(e) => {
                setPage(1)
                setStatus(e.target.value)
              }}
            >
              <option value="">All Statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>
          <div className="col-6 col-md-2">
            <select
              className="form-select form-select-sm"
              value={eventType}
              onChange={(e) => {
                setPage(1)
                setEventType(e.target.value)
              }}
            >
              <option value="">All Event Types</option>
              {KNOWN_EVENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <label
              className="form-label small visually-hidden"
              htmlFor="notif-start-date"
            >
              From
            </label>
            <input
              id="notif-start-date"
              type="date"
              className="form-control form-control-sm"
              value={startDate}
              onChange={(e) => {
                setPage(1)
                setStartDate(e.target.value)
              }}
            />
          </div>
          <div className="col-6 col-md-2">
            <label
              className="form-label small visually-hidden"
              htmlFor="notif-end-date"
            >
              To
            </label>
            <input
              id="notif-end-date"
              type="date"
              className="form-control form-control-sm"
              value={endDate}
              onChange={(e) => {
                setPage(1)
                setEndDate(e.target.value)
              }}
            />
          </div>
        </div>

        {error && <div className="alert alert-danger py-2">{error}</div>}

        {loading ? (
          <div className="text-muted small">Loading email activity...</div>
        ) : logs.length === 0 ? (
          <div className="alert alert-info py-2 mb-0">
            No auto-triggered email activity found for current filters.
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-2">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Recipient</th>
                    <th>Event</th>
                    <th>League</th>
                    <th>Status</th>
                    <th>Failure</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td className="text-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td>
                        <div className="fw-semibold">
                          {log.user_name || 'Unknown user'}
                        </div>
                        <div className="text-muted small">
                          {log.user_email || 'No email'}
                        </div>
                      </td>
                      <td>
                        <code>{log.event_type}</code>
                      </td>
                      <td>{log.league_name || '—'}</td>
                      <td>
                        <span
                          className={`badge ${log.status === 'sent' ? 'text-bg-success' : log.status === 'failed' ? 'text-bg-danger' : 'text-bg-secondary'}`}
                        >
                          {log.status}
                        </span>
                      </td>
                      <td className="small text-muted">
                        {log.failure_reason || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center small text-muted">
              <span>Total: {pagination.total || 0}</span>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => prev - 1)}
                >
                  Prev
                </button>
                <span>
                  Page {page} / {pagination.totalPages || 1}
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page >= (pagination.totalPages || 1)}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default NotificationEmailActivityPanel
